import React, {Dispatch, memo, SetStateAction} from 'react'
import {
  Driver,
  FeatureFlagAdmin,
  MainContact,
  Partner,
  SessionData,
  SimRigsData,
  SimulationDrive,
  User,
} from '../../services/api/api'
import {NavigateFunction} from 'react-router-dom'
import Icon from '../Icon'
import {IconSizes} from '../Icon/Icon'
import appStyle from '@yaak/admin/src/style.less'
import {toastType, ToastTypes} from '../Toast/Toast'
import AvatarNamePlate from '../AvatarNamePlate'
import Badge from '../Badge'
import {BadgeType} from '../Badge/Badge'
import Score from '@yaak/ds-admin/src/components/Score/Score'
import {toHoursAndMinutes} from '@yaak/ds-admin/src/helpers/helpers'
import style from '@yaak/ds-admin/src/pages/StudentsPage/style.less'
import classNames from 'classnames'
import ExamResultComponent from '@yaak/ds-admin/src/components/ExamResult/ExamResult'
import Typography from '../Typography'
import {TypographyTypes} from '../Typography/Typography'
import {FormattedMessage} from 'react-intl'
import PostAddIcon from "@mui/icons-material/PostAdd";
import Button from "../Button";

const mainContactRow = (mainContact: MainContact | null) =>
  mainContact ? (
    <AvatarNamePlate
      name={`${mainContact.firstName} ${mainContact.lastName}`}
      gravatarURL={mainContact.profileImageURL}
    />
  ) : (
    '—'
  )

const driverInstructorRow = (driver: Driver) =>
  driver ? (
    <AvatarNamePlate
      name={`${driver.firstname} ${driver.lastname}`}
      gravatarURL={driver.profileImageURL}
    />
  ) : (
    '—'
  )

const GridNameElement = ({
  data,
  ellipsis,
  tooltip,
  navigate,
}: {
  data: SimRigsData
  tooltip: string
  ellipsis: boolean
  navigate: NavigateFunction
}) => {
  return {
    text: data.name,
    tooltip: ellipsis ? data.name : tooltip,
    ellipsis,
    click: (event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      navigate(`/simulatorrigs/${data.id}`)
    },
  }
}

const GridPartnerElement = ({
  partner,
  ellipsis,
  navigate,
}: {
  partner?: Partner | null
  ellipsis?: boolean
  navigate?: NavigateFunction
}) => {
  return {
    text: partner ? partner.name : '-',
    tooltip: ellipsis
      ? partner?.name
      : partner?.name
        ? 'View partner profile'
        : '',
    ellipsis: true,
    click: (event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      partner && navigate?.(`/partners/${partner.id}/drives`)
    },
  }
}

const GridVehicleElement = ({
  vin,
  licensePlate,
  dongleId,
  ellipsis,
  navigate,
  dsAdmin,
}: {
  vin?: string
  licensePlate?: string
  dongleId?: string
  ellipsis: boolean
  navigate?: NavigateFunction
  dsAdmin?: boolean
}) => {
  return {
    text: licensePlate || vin || '-',
    tooltip: vin && navigate && 'View vehicle profile',
    ellipsis,
    click: (event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      dsAdmin
        ? navigate?.(`/vehicles/${dongleId}`)
        : navigate?.(`/vehicles/${vin}`)
    },
  }
}

const GridKitElement = ({
  id,
  ellipsis,
  navigate,
}: {
  id?: string
  ellipsis: boolean
  navigate?: NavigateFunction
}) => {
  return {
    text: id || '-',
    tooltip: ellipsis ? id : navigate && 'View kit profile',
    ellipsis: true,
    click: (event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      id && navigate?.(`/kits/details/${id}`)
    },
  }
}

const GridDriverElement = ({
  driver,
  ellipsis,
  navigate,
  dsAdmin,
  driveType,
}: {
  driver: Driver | null
  ellipsis: boolean
  navigate?: NavigateFunction
  dsAdmin?: boolean
  driveType?: number
}) => {
  return {
    text: driver ? driverInstructorRow(driver) : driver || '-',
    tooltip: driver
      ? `${driver?.firstname} ${driver?.lastname}`
      : navigate && 'View user profile',
    ellipsis,
    click: (event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      driver &&
        navigate?.(
          dsAdmin
            ? driveType === 2
              ? `/students/${driver.id}`
              : `/instructors/${driver.id}`
            : `/users/${driver.id}`
        )
    },
  }
}

const GridInstructorElement = ({
  session,
  ellipsis,
  navigate,
  dsAdmin,
}: {
  session: SessionData
  ellipsis: boolean
  navigate?: NavigateFunction
  dsAdmin: boolean
}) => {
  const instructor = session.instructor
    ? `${session.instructor?.firstname} ${session.instructor?.lastname}`
    : '-'
  return {
    text: session.instructor
      ? driverInstructorRow(session.instructor)
      : instructor,
    tooltip: session.instructor && instructor,
    ellipsis,
    click: (event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      session.instructor &&
        navigate?.(
          dsAdmin
            ? session.driveType === 2
              ? `/students/${session.instructor.id}`
              : `/instructors/${session.instructor.id}`
            : `/users/${session.instructor.id}`
        )
    },
  }
}

const GridMainContactElement = ({
  partner,
  navigate,
}: {
  partner: Partner | null
  navigate: NavigateFunction
}) => {
  return {
    text: partner && mainContactRow(partner.mainContact),
    tooltip: partner?.mainContact && 'View user profile',
    click: (event: React.MouseEvent) => {
      event.stopPropagation()
      event.preventDefault()
      partner && navigate?.(`/users/${partner.mainContactId}`)
    },
  }
}

const GridTextElement = ({
  text,
  ellipsis = false,
}: {
  text: string | React.ReactNode
  ellipsis?: boolean
}) => {
  let treatedText: string | React.ReactNode = '-'

  if (typeof text === 'string') {
    treatedText = text.trim() !== '' ? text : treatedText;
  } else if (React.isValidElement(text)) {
    treatedText = text;
  }

  return {
    text: treatedText,
    ellipsis,
    tooltip: ellipsis && text,
  }
}

export type GridCopyActionData =
  | SimRigsData
  | Partner
  | SessionData
  | User
  | SimulationDrive

const CopyIcon = ({
  text,
  data,
  setShowToast,
}: {
  data: GridCopyActionData
  setShowToast: Dispatch<SetStateAction<toastType | null>>
  text?: string
}) => {
  return (
    <Icon
      name={'Copy'}
      size={IconSizes.medium}
      className={appStyle.pointer}
      onClick={(event: React.MouseEvent<SVGSVGElement>) => {
        event.stopPropagation()
        event.preventDefault()
        navigator.clipboard.writeText(data.id)
        setShowToast({ text: text || `ID Copied`, type: ToastTypes.success })
      }}
    />
  )
}

const MemoCopyIcon = memo(CopyIcon)

const GridCopyIconElement = ({
  data,
  name,
  setShowToast,
  text,
}: {
  data: GridCopyActionData
  name: string
  setShowToast: Dispatch<SetStateAction<toastType | null>>
  text?: string
}) => {
  return {
    icon: true,
    tooltip: `Copy ${name} ID`,
    text: <MemoCopyIcon text={text} setShowToast={setShowToast} data={data} />,
  }
}

export type GridEditActionData = Partner | SimRigsData | User | FeatureFlagAdmin | string

const EditIcon = ({
  data,
  editAction,
}: {
  data: GridEditActionData
  editAction: (data: any) => void
}) => {
  const edit = (event: any) => {
    event.stopPropagation()
    event.preventDefault()
    editAction(data)
  }
  return (
    <Icon
      name={'Edit'}
      className={appStyle.pointer}
      onClick={edit}
      size={IconSizes.medium}
    />
  )
}

const MemoEditIcon = memo(EditIcon)

const GridEditIconElement = ({
  data,
  editAction,
  name,
  message,
}: {
  data: GridEditActionData
  editAction: (data: any) => void
  name?: string
  message?: React.ReactNode
}) => {
  return {
    icon: true,
    hidden: false,
    tooltip: message || `Edit ${name}`,
    text: <MemoEditIcon data={data} editAction={editAction} />,
  }
}

const GridToggleIconElement = ({
  data,
  toggleAction,
  tooltip,
}: {
  data: FeatureFlagAdmin
  toggleAction: (data: FeatureFlagAdmin) => void
  tooltip?: React.ReactNode | string
}) => {
  return {
    icon: true,
    hidden: false,
    tooltip,
    text: (
      <Button
        text={'Toggle'}
        className={classNames(
          appStyle.pointer,
        )}
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
          window.confirm(`Are you sure you want to change the state of the feature flag ${data.name} to ${!data.enabled}?`) && toggleAction(data)
        }}
      />
    ),
  }
}

const GridDeleteIconElement = ({
  data,
  deleteAction,
  tooltip,
}: {
  data: User | SimulationDrive
  deleteAction: (data: any) => void
  tooltip?: React.ReactNode | string
}) => {
  return {
    icon: true,
    hidden: false,
    tooltip,
    text: (
      <Icon
        name={'Delete'}
        className={classNames(
          appStyle.pointer,
        )}
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
          deleteAction(data)
        }}
        size={IconSizes.medium}
      />
    ),
  }
}

const GridPasswordResetIconElement = ({
  data,
  resetPasswordAction,
  tooltip,
}: {
  data: User
  resetPasswordAction: (data: User) => void
  tooltip?: React.ReactNode | string
}) => {
  return {
    icon: true,
    hidden: false,
    tooltip,
    text: (
      <Icon
        name={'Refresh'}
        className={classNames(
          appStyle.pointer,
        )}
        onClick={(event) => {
          event.stopPropagation()
          event.preventDefault()
          resetPasswordAction(data)
        }}
        size={IconSizes.medium}
      />
    ),
  }
}

const GridActiveElement = ({ condition }: { condition: boolean }) => {
  return {
    text: condition ? (
      <Badge label={'Active'} withDot />
    ) : (
      <Badge label={'Inactive'} type={BadgeType.grey} withDot />
    ),
  }
}

const GridIconElement = ({
  tooltip,
  text,
}: {
  tooltip?: string
  text: string | React.ReactNode
}) => {
  return {
    icon: true,
    tooltip,
    text,
  }
}

const GridDriveStatusElement = ({
  session,
  activeDrivesIds,
}: {
  session: SessionData
  activeDrivesIds: string[]
}) => {
  return {
    text:
      activeDrivesIds.indexOf(session.id) !== -1 ? (
        <Icon
          name={'ArrowForward'}
          styles={{ color: '#0070cc' }}
          size={IconSizes.medium}
        />
      ) : (
        <Icon
          name={'Success'}
          styles={{ color: '#00A751' }}
          size={IconSizes.medium}
        />
      ),
    tooltip:
      activeDrivesIds.indexOf(session.id) !== -1 ? 'In progress' : 'Completed',
    icon: true,
  }
}

const BitRateGridElement = ({ session }: { session: SessionData }) => {
  return GridIconElement({
    tooltip: session.bitRateKbps ? session.bitRateKbps.toString() : '-',
    text: session.bitRateKbps ? (
      session.bitRateKbps >= 8000 ? (
        <Badge label={`${(session.bitRateKbps / 1024).toFixed(0)} Mbps`} />
      ) : (
        <Badge
          label={`${(session.bitRateKbps / 1024).toFixed(0)} Mbps`}
          type={BadgeType.yellow}
        />
      )
    ) : (
      '-'
    ),
  })
}

const GridUserElement = ({
  user,
  ellipsis,
  navigate,
}: {
  navigate?: NavigateFunction
  user: User
  ellipsis?: boolean
}) => {
  return {
    text: (
      <AvatarNamePlate
        name={`${user.firstName} ${user.lastName}`}
        gravatarURL={user.profileImageURL}
      />
    ),
    tooltip: ellipsis
      ? `${user.firstName} ${user.lastName}`
      : 'Open user profile',
    click: () => navigate?.(`/users/${user.id}`),
  }
}

const GridUserNoInteractionElement = ({
   user,
 }: {
  user: User
}) => {
  return {
    text: (
      <AvatarNamePlate
        name={`${user.firstName} ${user.lastName}`}
        gravatarURL={user.profileImageURL}
      />
    ),
  }
}

export const GridScoreElement = ({ user }: { user: User }) => {
  return GridIconElement({
    text: user.rating ? (
      <Score active={user.active} score={user.rating} />
    ) : (
      <></>
    ),
  })
}

const GridTotalTimeElement = ({ time }: { time?: number }) => {
  return GridTextElement({
    text: toHoursAndMinutes(time),
  })
}

const GridSuccessCancelIconElement = ({
  condition,
  positive,
  negative,
}: {
  condition?: boolean
  positive: string
  negative: string
}) => {
  return GridIconElement({
    tooltip: condition ? positive : negative,
    text: condition ? (
      <Icon
        className={style.success}
        name={'Success'}
        size={IconSizes.medium}
      />
    ) : (
      <Icon className={style.cancel} name={'Cancel'} size={IconSizes.medium} />
    ),
  })
}

interface ExamCellOptions {
  setOpenAddExamResults: (value: boolean) => void
  setExamResultsFor: (value: User) => void
}

const examCell = (user: User, options: ExamCellOptions) => {
  const unfinishedLicenses = (user.licenses ?? []).filter((license) => license.finishedAt == null)
  const containerCssClass = unfinishedLicenses.length > 0 ? style.examResultContainer : style.examResultContainerNoLicenseLeft
  return <div
    className={classNames(
      style.addExam,
      user.active ? style.hideOnOver : undefined
    )}
  >
    <div className={classNames(style[containerCssClass])}>
      <>
        <ExamResultComponent
          examResults={user.examResults}
        />
      </>
    </div>
    {user.active && unfinishedLicenses.length > 0 && (
      <Typography
        type={TypographyTypes.link}
        className={style.addExamLink}
        onClick={(event) => {
          event.stopPropagation()
          options.setOpenAddExamResults(true)
          options.setExamResultsFor(user)
        }}
      >
        <Badge label={<><PostAddIcon/> <FormattedMessage id={'add_exam'} /></>} type={BadgeType.blue}/>
      </Typography>
    )}
  </div>
}

const GridExamCellElement = ({
  user,
  options,
}: {
  user: User
  options: ExamCellOptions
}) => {
  return GridIconElement({
    text: examCell(user, options),
  })
}

const isUserAdminInstructor = (user: User) =>
  user.roles?.indexOf('ds-admin') !== -1 &&
  user.roles?.indexOf('ds-instructor') !== -1

const GridStartElement = ({ user }: { user: User }) => {
  return GridIconElement({
    text: isUserAdminInstructor(user) ? (
      <Icon name={'StarFill'} styles={{ color: '#E5B300' }} />
    ) : (
      <></>
    ),
  })
}

export {
  GridStartElement,
  GridExamCellElement,
  GridNameElement,
  GridPartnerElement,
  GridVehicleElement,
  GridKitElement,
  GridDriverElement,
  GridInstructorElement,
  GridMainContactElement,
  GridTextElement,
  GridCopyIconElement,
  GridDeleteIconElement,
  GridPasswordResetIconElement,
  GridActiveElement,
  GridIconElement,
  GridDriveStatusElement,
  BitRateGridElement,
  GridEditIconElement,
  GridSuccessCancelIconElement,
  GridTotalTimeElement,
  GridToggleIconElement,
  GridUserElement,
  GridUserNoInteractionElement
}
